import { useState } from 'react';
import { Stack, Group, Card, Title, Text, Button, ActionIcon, Center, Badge, SegmentedControl } from '@mantine/core';
import { IconArrowLeft, IconPhoto, IconTrash, IconPencil } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { confirmDelete } from '../lib/confirm';
import { BannerPreview } from '../components/BannerPreview';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { Lang, LANGS } from '../lib/bannerDesign';
import { BannerBuilder, Banner, BannerPayload, audMeta, audienceLabel, KioskBadge, themeSwitchData } from '../components/BannerBuilder';

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** Banner detali — ko'rish (preview) + tahrirlash (konstruktor) + o'chirish (bir sahifada). */
export default function BannerDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [prevTheme, setPrevTheme] = useState<'light' | 'dark'>('light');
  const [prevLang, setPrevLang] = useState<Lang>('uz');

  const { data: banner, isLoading } = useQuery({
    queryKey: ['banner', id],
    queryFn: async () => (await api.get<Banner>(`/banners/${id}`)).data,
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['banner', id] });
    qc.invalidateQueries({ queryKey: ['banners'] });
  };

  const saveMut = useMutation({
    mutationFn: (payload: BannerPayload) => api.patch(`/banners/${id}`, payload),
    onSuccess: () => { notifications.show({ color: 'green', message: t('bannersPage.saved') }); invalidate(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/banners/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('bannersPage.deleted') }); invalidate(); navigate('/banners'); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!banner) return <Center h={300}><Text c="dimmed">{t('bannerDetail.notFound')}</Text></Center>;

  const m = audMeta(banner.audience);

  return (
    <Stack>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/banners')}><IconArrowLeft size={18} /></ActionIcon>
        <Group gap="xs"><IconPhoto size={22} color="var(--mantine-color-brand-6)" /><Title order={4}>{banner.title}</Title></Group>
      </Group>

      {/* Ko'rish (preview) */}
      <SectionCard
        title={t('bannerDetail.preview')}
        icon={IconPhoto}
        right={
          <Group gap="xs" justify="flex-end" wrap="wrap">
            <Badge variant="light" color={m.color}>{audienceLabel(t, banner.audience)}</Badge>
            <Badge variant="light" color={banner.isActive ? 'green' : 'gray'}>
              {banner.isActive ? t('bannerDetail.active') : t('bannerDetail.inactive')}
            </Badge>
            {/* Ro'yxatdagi bilan bir xil belgi: auditoriya «Hammaga» bo'lmasa banner TV'da
                CHIQMAYDI — belgi shuni ochiq aytadi (eski yozuvlarda uchraydi). */}
            <KioskBadge banner={banner} />
            <SegmentedControl size="xs" value={prevTheme} onChange={(v) => setPrevTheme(v as 'light' | 'dark')}
              data={themeSwitchData(t)} />
            <SegmentedControl size="xs" value={prevLang} onChange={(v) => setPrevLang(v as Lang)}
              data={LANGS.map((l) => ({ value: l, label: l.toUpperCase() }))} />
          </Group>
        }
      >
        <Center>
          <BannerPreview banner={banner} theme={prevTheme} lang={prevLang} width={320} />
        </Center>
        {banner.createdBy && (
          <Text size="xs" c="dimmed" mt="sm" ta="center">{t('bannerDetail.createdBy')}: {banner.createdBy.fullName}</Text>
        )}
      </SectionCard>

      {/* Tahrirlash */}
      <SectionCard title={t('bannerDetail.edit')} icon={IconPencil}>
        <BannerBuilder
          key={banner.id}
          initial={banner}
          onSubmit={(payload) => saveMut.mutate(payload)}
          submitting={saveMut.isPending}
        />
      </SectionCard>

      {/* O'chirish */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('bannerDetail.deleteHint')}</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(banner.title, () => deleteMut.mutate())} loading={deleteMut.isPending}>
            {t('common.delete')}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
